import type { FirstPartyDeviceType, ExternalLink, ThirdPartyDeviceType } from "verdi-common";
import { ModelBase } from "../ModelBase.js";
import { DeviceWarning } from "./Warnings.js";

export class DeviceBase extends ModelBase {
    createdAt: Date = new Date(0);
    checkForRecentData = true;
    /**
     *
     * @type {DeviceWarning[]}
     */
    warnings: DeviceWarning[] = [];
    /**
     * Coordinate of device
     * @type number
     */
    lat: number;
    /**
     * @type {number}
     */
    long: number;
    adminNotes: string;
    notes: string;
    externalLinks: Record<string, ExternalLink>;

    get lng(): number {
        return this.long;
    }
    set lng(v: number) {
        this.long = v;
    }
    /**
     * Coordinates of device
     * @type {{lat: Number, lng: Number}}
     * @deprecated
     */
    location = { lat: 0, lng: 0 };

    /**
     * the type of device
     * @type {"unknown" | "sap_1v" | "sap_2v" | "sap_sense" | "spr_1v" | "spr_2v" | "spr_sense" | "spr_block" | "gen4_block"}
     */
    _type: FirstPartyDeviceType | "msense" | "unknown" | "beta1v" | ThirdPartyDeviceType;
    set type(v: FirstPartyDeviceType | "msense" | "unknown" | "beta1v" | ThirdPartyDeviceType) {
        this._type = v;
    }
    get type(): FirstPartyDeviceType | "msense" | "unknown" | "beta1v" | ThirdPartyDeviceType {
        return this._type;
    }
    //type: FirstPartyDeviceType | "msense" | "unknown" | "beta1v" | ThirdPartyDeviceType = "unknown";
    /**
     * @deprecated Only set for a subset of sapling device types and never set
     * for sprout/gen4 variants. Derive valve information from
     * `DeviceTypeConfigurations[type]` instead.
     */
    singleValve = false;
    /**
     * @deprecated Only set for a subset of sapling device types and never set
     * for sprout/gen4 variants. Derive valve information from
     * `DeviceTypeConfigurations[type]` instead.
     */
    doubleValve = false;
    /**
     * @deprecated Always `false` after retired seed/sap block types were
     * removed. Detect block-valve devices via
     * `DeviceTypeConfigurations[type]?.valveType === "block"`
     * or via the `BlockValve` model category instead.
     */
    blockValve = false;
    sensoterraDev = false;

    /**
     * @type {{
     * downlinkPacketLossRatio : Number,
     * errorRatio: Number,
     * packetLossRatio: Number,
     * uplinkPacketLossRatio: Number,
     * batt: Number
     * } | {}}
     */
    networkStats = {};

    constructor(json) {
        super(json);
        try {
            if (json.warnings) {
                this.warnings = json.warnings.map((w) => new DeviceWarning(w));
            }
            this.warnings.sort((a, b) => {
                const aScore = a.warningLevel * 10000 + a.warningTypePriority;
                const bScore = b.warningLevel * 10000 + b.warningTypePriority;
                return bScore - aScore;
            });
        } catch (e) {
            console.warn(e);
        }
    }

    assignFromJSON(json) {
        super.assignFromJSON(json);
        this.name = json.name || json.label || json.node_id || this.name;
        this.lat = json.latitude || this.lat;
        this.long = json.longitude || this.long;
        this.notes = json.notes ?? this.notes ?? "";
        this.adminNotes = json.adminNotes ?? this.adminNotes ?? "";
        this.connectedEquipment = json.connectedEquipment ?? this.connectedEquipment ?? "valve";
        this.externalLinks = json.externalLinks ?? this.externalLinks ?? {};
        this.verdiId = json.verdiId ?? this.verdiId;
        if (json.createdAt) {
            this.createdAt = new Date(json.createdAt);
        }
        if (json.DeviceType) {
            this.type = json.DeviceType;
        }
        this.singleValve = ["sap_1v", "sap_sense"].includes(this.type);
        this.doubleValve = this.type === "sap_2v";
        this.blockValve = false;
        this.sensoterraDev = this.type === "msense";
    }

    connectedEquipment: "valve" | "pump" = "valve";

    /**
     * @type {string}
     */
    enclosureID;

    verdiId?: string;

    history;
    device_stats;
    status;
}
